/**
 * Catalogue toolbar — search + sort (Shopify collection header pattern).
 */

import { Search } from "lucide-react";
import type { BrowseFilters } from "@/components/browse/FilterRail";
import { BROWSE_PAGE_SIZE_OPTIONS, BROWSE_SORT_OPTIONS } from "@/lib/browseCatalog";

interface BrowseCatalogToolbarProps {
  filters: BrowseFilters;
  onChange: (next: BrowseFilters) => void;
  totalCount: number | null;
  loading: boolean;
  variant?: "default" | "compact";
}

export function BrowseCatalogToolbar({
  filters,
  onChange,
  totalCount,
  loading,
  variant = "default",
}: BrowseCatalogToolbarProps) {
  const sortId = variant === "compact" ? "browse-sort-compact" : "browse-sort";
  const pageSizeId = variant === "compact" ? "browse-page-size-compact" : "browse-page-size";

  const rangeLabel =
    !loading && totalCount !== null && totalCount > 0
      ? (() => {
          const start = (filters.page - 1) * filters.pageSize + 1;
          const end = Math.min(filters.page * filters.pageSize, totalCount);
          return `${start}–${end} of ${totalCount}`;
        })()
      : null;

  return (
    <div
      className={
        variant === "compact"
          ? "mb-3 flex flex-col gap-2 sm:flex-row sm:items-center"
          : "mb-4 rounded-2xl border border-charcoal/10 bg-white px-4 py-3 sm:px-5"
      }
      style={{ fontFamily: "DM Sans, sans-serif" }}
    >
      <div
        className={
          variant === "compact"
            ? "flex w-full flex-col gap-2 sm:flex-row sm:items-center"
            : "flex w-full flex-col gap-3 lg:flex-row lg:items-end lg:justify-between"
        }
      >
      <label className="relative min-w-0 flex-1">
        <span className="sr-only">Search catalogue</span>
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/45"
          aria-hidden
        />
        <input
          type="search"
          value={filters.searchQuery}
          onChange={(e) => onChange({ ...filters, searchQuery: e.target.value })}
          placeholder="Search by product or vendor"
          className="w-full rounded-full border border-charcoal/15 bg-white py-2 pl-9 pr-4 text-sm text-navy placeholder:text-charcoal/45 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
          data-testid="browse-search-input"
        />
      </label>

      <div className="flex shrink-0 flex-wrap items-center gap-2 lg:gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor={sortId} className="text-xs font-semibold uppercase tracking-wide text-charcoal/70">
            Sort
          </label>
          <select
            id={sortId}
            value={filters.sort}
            onChange={(e) =>
              onChange({ ...filters, sort: e.target.value as BrowseFilters["sort"] })
            }
            className="rounded-full border border-charcoal/15 bg-white px-3 py-2 text-sm text-navy focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
            data-testid="browse-sort-select"
          >
            {BROWSE_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {variant === "default" && (
          <div className="flex items-center gap-2">
            <label htmlFor={pageSizeId} className="text-xs font-semibold uppercase tracking-wide text-charcoal/70">
              Show
            </label>
            <select
              id={pageSizeId}
              value={filters.pageSize}
              onChange={(e) =>
                onChange({
                  ...filters,
                  pageSize: Number(e.target.value) as BrowseFilters["pageSize"],
                  page: 1,
                })
              }
              className="rounded-full border border-charcoal/15 bg-white px-3 py-2 text-sm text-navy focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
              data-testid="browse-page-size-select"
            >
              {BROWSE_PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {rangeLabel && variant === "default" && (
          <span className="hidden text-xs font-medium text-charcoal/70 xl:inline" data-testid="browse-range-label">
            {rangeLabel}
          </span>
        )}
      </div>
      </div>
    </div>
  );
}
